import { NO_INTENT, type FighterInputSource, type FighterIntent } from './fighterInput';

/**
 * Several devices driving one fighter at once.
 *
 * A player at a desk has a keyboard and a mouse and may also have a pad plugged in, and should not
 * have to tell the game which one they are about to use: picking up the controller mid-duel simply
 * works, and so does dropping it again.
 *
 * Every source is polled and asked for its intent every step, even ones doing nothing, because a
 * `Latch` reports a press exactly once — skipping a source would swallow a tap made on it.
 */
export function createCompositeFighterInput(
  sources: readonly FighterInputSource[],
): FighterInputSource {
  return {
    update(dt) {
      for (const source of sources) source.update(dt);
    },
    next(mode): FighterIntent {
      const intent: FighterIntent = { ...NO_INTENT };
      let hardest = 0;
      for (const source of sources) {
        const from = source.next(mode);
        // The hardest push wins rather than the sum: leaning on two sticks at once is one lean.
        const push = Math.hypot(from.moveX, from.moveZ);
        if (push > hardest) {
          hardest = push;
          intent.moveX = from.moveX;
          intent.moveZ = from.moveZ;
        }
        intent.aimDir ??= from.aimDir;
        intent.aimAt ??= from.aimAt;
        intent.dash ||= from.dash;
        intent.attack ||= from.attack;
        if (from.forfeit) intent.forfeit = true;
        if (Math.abs(from.steer ?? 0) > Math.abs(intent.steer ?? 0)) intent.steer = from.steer;
      }
      // A stick pushed is a deliberate aim; a cursor merely lying somewhere on the screen is not,
      // and it lies somewhere at all times. So the stick wins when a player has both.
      if (intent.aimDir) intent.aimAt = null;
      return intent;
    },
    // A pad in a desk player's hands gives up by its Start held, and the panel says how far
    // (client-input:quality#2). The keyboard beside it is always there, so the pair is too.
    forfeitHold: () => {
      for (const source of sources) {
        const hold = source.forfeitHold?.() ?? null;
        if (hold) return hold;
      }
      return null;
    },
    pulse(strength, seconds) {
      for (const source of sources) source.pulse?.(strength, seconds);
    },
    dispose() {
      for (const source of sources) source.dispose();
    },
  };
}
